import React, { useEffect, useState } from "react";
import { Button, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import ContractTemplatesModal from "~/pages/Editor/ContractTemplates/modals/ContractTemplatesModal";
import FieldIdentifiersModal from "~/pages/Editor/ContractTemplates/modals/FieldIdentifiersModal";
import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  ContractTemplateTableRow,
  getContractTemplatesColumns,
} from "~/pages/Editor/ContractTemplates/model/tableAdapter";
import {
  SortInputOrder,
  useGetContractTemplatesLazyQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";

import s from "./s.less";

const SORTABLE_COLUMNS = ["active"];

const ContractTemplates = () => {
  const { t } = useTranslation();
  const {
    page,
    count,
    sortField: sortFieldFromUrl,
    sortType: sortTypeRaw,
  } = useGetLocationData();
  const setData = useSetLocationData();
  const setDataReplace = useSetLocationData(true);
  const { hasPermission } = usePermissions();

  const history = useHistory();
  const [isFieldIdentifiersModalOpen, setIsFieldIdentifiersModalOpen] =
    useState(false);

  const goBack = () => history.replace("/editor/");

  const { sortType, onSortChange } = useTableSort({
    sortField: sortFieldFromUrl,
    sortType: sortTypeRaw,
    sortableColumns: SORTABLE_COLUMNS,
    setData,
  });

  const tableSortField = SORTABLE_COLUMNS.includes(sortFieldFromUrl)
    ? sortFieldFromUrl
    : undefined;
  const hasSortParams = Boolean(tableSortField && sortType);

  const openAddModal = () => {
    setDataReplace("openModal", "Create");
  };

  const toggleFieldIdentifiersModal = () => {
    setIsFieldIdentifiersModalOpen(!isFieldIdentifiersModalOpen);
  };

  const [getContractTemplates, getContractTemplatesState] =
    useGetContractTemplatesLazyQuery({
      fetchPolicy: "no-cache",
    });

  const getContractTemplateHandler = () => {
    getContractTemplates({
      variables: {
        page: Number(page),
        count: Number(count),
        ...(hasSortParams && tableSortField
          ? {
              sort: [
                {
                  column: String(tableSortField),
                  order:
                    sortType === "ASC"
                      ? SortInputOrder.Asc
                      : SortInputOrder.Desc,
                },
              ],
            }
          : {}),
      },
    });
  };

  const contracts = (
    get(
      getContractTemplatesState.data,
      "ContractTemplateGetter.getContractTemplates.items",
      [],
    ) as ContractTemplateTableRow[]
  ).map((el) => {
    return {
      ...el,
      active: el.active ? "АКТИВНЫЙ" : "НЕАКТИВНЫЙ",
      link:
        window.location.origin +
        (el as { File?: { link?: string } }).File?.link,
    };
  });

  const total = get(
    getContractTemplatesState.data,
    "ContractTemplateGetter.getContractTemplates.total",
  );

  useEffect(() => {
    getContractTemplateHandler();
  }, [sortFieldFromUrl, sortTypeRaw, page, count]);

  const handleRowEdit = (record: ContractTemplateTableRow) => {
    setDataReplace("openModal", "Edit", "currentId", record.id);
  };

  const { handleCount, handlePage } = usePaginationParams();

  return (
    <Wrapper.Content
      title={t("contractTemplates.editor_title")}
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("contracts.createTpm") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("contractTemplates.catalogTemplate")}
            </Button>
          )}
          <Button onClick={toggleFieldIdentifiersModal} size={"middle"}>
            {t("contractTemplates.identifiers")}
          </Button>
        </Flex>
      }
    >
      <TableV2<ContractTemplateTableRow>
        wrapperClassName={s.tableWrapper}
        columns={getContractTemplatesColumns(t, {
          sortField: tableSortField,
          sortType: tableSortField ? sortType : undefined,
        })}
        dataSource={contracts}
        loading={getContractTemplatesState.loading}
        rowKey="id"
        onRowEdit={handleRowEdit}
        sortableColumns={SORTABLE_COLUMNS}
        sortField={tableSortField}
        sortType={tableSortField ? sortType : undefined}
        onSortChange={onSortChange}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <ContractTemplatesModal
        contracts={contracts}
        reFetch={getContractTemplateHandler}
      />
      <FieldIdentifiersModal
        isOpen={isFieldIdentifiersModalOpen}
        onClose={toggleFieldIdentifiersModal}
      />
    </Wrapper.Content>
  );
};

export default ContractTemplates;
